import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil } from 'lucide-react';
import { userHooks, locationHooks } from '../../hooks/useMasters.js';
import { api } from '../../utils/api.js';
import { useQueryClient } from '@tanstack/react-query';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { ROLE_LABELS } from '../../utils/constants.js';

const createSchema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().email('Valid email required'),
  password: z.string().min(8, 'Minimum 8 characters'),
  role: z.string().min(1, 'Required'),
});

export default function UsersPage() {
  const { data: users = [], isLoading } = userHooks.useList({ includeInactive: 'true' });
  const { data: locations = [] } = locationHooks.useList();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selLocs, setSelLocs] = useState([]);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(editing ? createSchema.omit({ password: true }) : createSchema),
  });

  const openCreate = () => { setEditing(null); setSelLocs([]); reset({}); setModalOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setSelLocs(u.locations?.map(ul => ul.locationId) || []);
    reset({ name: u.name, email: u.email, role: u.role });
    setModalOpen(true);
  };

  const toggleLoc = (id) => setSelLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const onSubmit = async (values) => {
    const payload = { ...values, locationIds: selLocs };
    if (editing) await api.put(`/masters/users/${editing.id}`, payload);
    else await api.post('/auth/register', payload);
    qc.invalidateQueries({ queryKey: ['users'] });
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-sm text-gray-500">Create and manage system users</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add User</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Name','Email','Role','Locations','Status',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3"><span className="badge badge-checked">{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{u.locations?.map(ul => ul.location?.code).join(', ') || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={"badge " + (u.isActive ? 'badge-approved' : 'badge-cancelled')}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(u)} className="text-gray-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit User' : 'New User'} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Full Name" required error={errors.name?.message} {...register('name')} />
            <Input label="Email" type="email" required error={errors.email?.message} {...register('email')} />
          </div>
          {!editing && <Input label="Password" type="password" required error={errors.password?.message} {...register('password')} />}
          <Select label="Role" required error={errors.role?.message} {...register('role')}>
            <option value="">Select role…</option>
            {Object.entries(ROLE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Locations</label>
            <div className="grid grid-cols-2 gap-2 border rounded-lg p-3 max-h-40 overflow-y-auto">
              {locations.map(loc => (
                <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={selLocs.includes(loc.id)} onChange={() => toggleLoc(loc.id)} className="rounded" />
                  <span>{loc.code} — {loc.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>{editing ? 'Save Changes' : 'Create User'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
